import type {
  PutRateLimitRequest,
  RoutingLoadSettingsValues,
} from '@nadoutong/contracts';
import { parseTpmM, parseTpmMPreserving } from '../quotas/tpmUnits.ts';

export const LAYER_SWITCH_FIELDS = [
  'events_enabled',
  'l2_enabled',
  'l3_enabled',
  'l4_enabled',
] as const;

export type LayerSwitchField = (typeof LAYER_SWITCH_FIELDS)[number];

export const BOOLEAN_PARAM_FIELDS = [
  'l2_observed_caps_enabled',
  'l3_inferred_caps_enabled',
  'affinity_conversation_enabled',
  'session_rehome_enabled',
] as const;

export type BooleanParamField = (typeof BOOLEAN_PARAM_FIELDS)[number];

export const LOAD_NUMERIC_FIELDS = [
  'shed_step',
  'shed_quiet_seconds',
  'shed_decay',
  'half_open_trials',
  'shed_warm_threshold',
  'shed_warm_429_ratio',
  'session_bucket_rpm',
  'shed_target_ema_alpha',
  'shed_tick_seconds',
  'attempt_first_byte_timeout_ms',
  'sticky_escape_ttft_ms',
  'sticky_escape_error_rate',
  'attempt_timeout_ttft_multiplier',
  'attempt_timeout_max_multiplier',
  'session_rehome_after_429',
  'session_rehome_ttl_seconds',
  'pool_key_rpm_ceiling',
  'lifecycle_sample_p_jp',
  'lifecycle_sample_p_cn',
  'lifecycle_keep_slow_ms',
] as const;

export type LoadNumericField = (typeof LOAD_NUMERIC_FIELDS)[number];

export type NumericEmptySemantics = 'off' | 'default';
export type NumericKind = 'int' | 'decimal';
export type NumericEmptyPlaceholder = 'keep_all' | 'off';

export interface NumericFieldMeta {
  readonly kind: NumericKind;
  readonly empty: NumericEmptySemantics;
  readonly minExclusive: number;
  readonly maxInclusive: number;
  readonly suggested: number | null;
  readonly emptyPlaceholder?: NumericEmptyPlaceholder;
}

export const TIMEOUT_ENABLE_PREFILL_MS = 30_000;

const INT_MIN = 0;
const UNIT_MAX = 1;
const DECAY_MAX = 0.999999;
const MULTIPLIER_MAX = 100;

const NUMERIC_META: Record<LoadNumericField, NumericFieldMeta> = {
  shed_step: { kind: 'decimal', empty: 'default', minExclusive: 0, maxInclusive: UNIT_MAX, suggested: 0.1 },
  shed_quiet_seconds: { kind: 'int', empty: 'default', minExclusive: INT_MIN, maxInclusive: Number.POSITIVE_INFINITY, suggested: 20 },
  shed_decay: { kind: 'decimal', empty: 'default', minExclusive: 0, maxInclusive: DECAY_MAX, suggested: 0.5 },
  half_open_trials: { kind: 'int', empty: 'default', minExclusive: INT_MIN, maxInclusive: Number.POSITIVE_INFINITY, suggested: 3 },
  shed_warm_threshold: { kind: 'decimal', empty: 'default', minExclusive: 0, maxInclusive: UNIT_MAX, suggested: 0.8 },
  shed_warm_429_ratio: { kind: 'decimal', empty: 'default', minExclusive: 0, maxInclusive: UNIT_MAX, suggested: 0.05 },
  session_bucket_rpm: { kind: 'int', empty: 'default', minExclusive: INT_MIN, maxInclusive: Number.POSITIVE_INFINITY, suggested: 60 },
  shed_target_ema_alpha: { kind: 'decimal', empty: 'default', minExclusive: 0, maxInclusive: UNIT_MAX, suggested: 0.3 },
  shed_tick_seconds: { kind: 'int', empty: 'default', minExclusive: INT_MIN, maxInclusive: Number.POSITIVE_INFINITY, suggested: 5 },
  attempt_first_byte_timeout_ms: {
    kind: 'int',
    empty: 'off',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: TIMEOUT_ENABLE_PREFILL_MS,
  },
  sticky_escape_ttft_ms: {
    kind: 'int',
    empty: 'off',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: 15_000,
  },
  sticky_escape_error_rate: { kind: 'decimal', empty: 'off', minExclusive: 0, maxInclusive: UNIT_MAX, suggested: 0.5 },
  attempt_timeout_ttft_multiplier: {
    kind: 'decimal',
    empty: 'default',
    minExclusive: 0,
    maxInclusive: MULTIPLIER_MAX,
    suggested: 2,
  },
  attempt_timeout_max_multiplier: {
    kind: 'decimal',
    empty: 'default',
    minExclusive: 0,
    maxInclusive: MULTIPLIER_MAX,
    suggested: 3,
  },
  session_rehome_after_429: {
    kind: 'int',
    empty: 'default',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: 2,
  },
  session_rehome_ttl_seconds: {
    kind: 'int',
    empty: 'default',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: 120,
  },
  pool_key_rpm_ceiling: {
    kind: 'int',
    empty: 'default',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: 120,
  },
  lifecycle_sample_p_jp: {
    kind: 'decimal',
    empty: 'default',
    minExclusive: 0,
    maxInclusive: UNIT_MAX,
    suggested: 1,
    emptyPlaceholder: 'keep_all',
  },
  lifecycle_sample_p_cn: {
    kind: 'decimal',
    empty: 'default',
    minExclusive: 0,
    maxInclusive: UNIT_MAX,
    suggested: 1,
    emptyPlaceholder: 'keep_all',
  },
  lifecycle_keep_slow_ms: {
    kind: 'int',
    empty: 'off',
    minExclusive: INT_MIN,
    maxInclusive: Number.POSITIVE_INFINITY,
    suggested: null,
    emptyPlaceholder: 'off',
  },
};

export function numericFieldMeta(field: LoadNumericField): NumericFieldMeta {
  return NUMERIC_META[field];
}

export type ParsedNumericInput =
  | { kind: 'unchanged' }
  | { kind: 'clear' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid' };

const INT_PATTERN = /^\d+$/;
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

export function parseNumericInput(
  field: LoadNumericField,
  raw: string,
  storedValue: number | null,
): ParsedNumericInput {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return storedValue === null ? { kind: 'unchanged' } : { kind: 'clear' };
  }
  const meta = NUMERIC_META[field];
  if (meta.kind === 'int') {
    if (!INT_PATTERN.test(trimmed)) return { kind: 'invalid' };
    const value = Number.parseInt(trimmed, 10);
    if (!Number.isInteger(value) || value <= meta.minExclusive || value > meta.maxInclusive) {
      return { kind: 'invalid' };
    }
    return value === storedValue ? { kind: 'unchanged' } : { kind: 'value', value };
  }
  if (!DECIMAL_PATTERN.test(trimmed)) return { kind: 'invalid' };
  const value = Number.parseFloat(trimmed);
  if (!Number.isFinite(value) || value <= meta.minExclusive || value > meta.maxInclusive) {
    return { kind: 'invalid' };
  }
  return value === storedValue ? { kind: 'unchanged' } : { kind: 'value', value };
}

export function buildNumericPatch(
  draft: Partial<Record<LoadNumericField, string>>,
  stored: Pick<RoutingLoadSettingsValues, LoadNumericField>,
): {
  readonly patch: Partial<Record<LoadNumericField, number | null>>;
  readonly errors: readonly LoadNumericField[];
} {
  const patch: Partial<Record<LoadNumericField, number | null>> = {};
  const errors: LoadNumericField[] = [];
  for (const field of LOAD_NUMERIC_FIELDS) {
    const raw = draft[field];
    if (raw === undefined) continue;
    const parsed = parseNumericInput(field, raw, stored[field]);
    if (parsed.kind === 'invalid') {
      errors.push(field);
      continue;
    }
    if (parsed.kind === 'clear') patch[field] = null;
    if (parsed.kind === 'value') patch[field] = parsed.value;
  }
  return { patch, errors };
}

export function parseKeepOrgIdsInput(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

export function keepOrgIdsDraftText(stored: readonly string[] | null): string {
  return (stored ?? []).join('\n');
}

export function buildKeepOrgIdsPatch(
  draft: string | undefined,
  stored: readonly string[] | null,
): { readonly unchanged: true } | { readonly unchanged: false; readonly value: readonly string[] | null } {
  if (draft === undefined) return { unchanged: true };
  const parsed = parseKeepOrgIdsInput(draft);
  const current = stored ?? [];
  if (parsed.length === current.length && parsed.every((id, index) => id === current[index])) {
    return { unchanged: true };
  }
  return { unchanged: false, value: parsed.length === 0 ? null : parsed };
}

export type LoadLayerId = 'events' | 'l2' | 'l3' | 'l4' | 'timeout';

export type LayerKillSwitchField =
  | 'l2_env_kill_switch'
  | 'l3_env_kill_switch'
  | 'l4_env_kill_switch'
  | 'session_rehome_env_kill_switch';

export interface LoadLayerGroup {
  readonly id: LoadLayerId;
  readonly switchField: LayerSwitchField | null;
  readonly killSwitch: LayerKillSwitchField | null;
  readonly visibleBooleans: readonly BooleanParamField[];
  readonly visibleNumerics: readonly LoadNumericField[];
  readonly advancedNumerics: readonly LoadNumericField[];
}

export const LOAD_LAYER_GROUPS: readonly LoadLayerGroup[] = [
  {
    id: 'events',
    switchField: 'events_enabled',
    killSwitch: null,
    visibleBooleans: [],
    visibleNumerics: [],
    advancedNumerics: [],
  },
  {
    id: 'l2',
    switchField: 'l2_enabled',
    killSwitch: 'l2_env_kill_switch',
    visibleBooleans: ['l2_observed_caps_enabled'],
    visibleNumerics: [],
    advancedNumerics: [],
  },
  {
    id: 'l3',
    switchField: 'l3_enabled',
    killSwitch: 'l3_env_kill_switch',
    visibleBooleans: ['l3_inferred_caps_enabled'],
    visibleNumerics: [],
    advancedNumerics: [
      'shed_step',
      'shed_quiet_seconds',
      'shed_decay',
      'half_open_trials',
      'shed_target_ema_alpha',
      'shed_tick_seconds',
    ],
  },
  {
    id: 'l4',
    switchField: 'l4_enabled',
    killSwitch: 'l4_env_kill_switch',
    visibleBooleans: ['affinity_conversation_enabled', 'session_rehome_enabled'],
    visibleNumerics: [],
    advancedNumerics: [
      'shed_warm_threshold',
      'shed_warm_429_ratio',
      'session_bucket_rpm',
      'sticky_escape_ttft_ms',
      'sticky_escape_error_rate',
      'session_rehome_after_429',
      'session_rehome_ttl_seconds',
      'pool_key_rpm_ceiling',
    ],
  },
  {
    id: 'timeout',
    switchField: null,
    killSwitch: null,
    visibleBooleans: [],
    visibleNumerics: ['attempt_first_byte_timeout_ms'],
    advancedNumerics: ['attempt_timeout_ttft_multiplier', 'attempt_timeout_max_multiplier'],
  },
];

export type ParsedCapInput =
  | { kind: 'empty' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid' };

export function parseRateLimitCap(raw: string): ParsedCapInput {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (!INT_PATTERN.test(trimmed)) return { kind: 'invalid' };
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(value) || value < 1) return { kind: 'invalid' };
  return { kind: 'value', value };
}

export type RateLimitDraftResult =
  | { kind: 'invalid'; rpm: boolean; tpm: boolean; tpmUncached: boolean }
  | { kind: 'delete' }
  | { kind: 'put'; body: PutRateLimitRequest };

export function isRateLimitDraftEmpty(rpmRaw: string, tpmRaw: string): boolean {
  return parseRateLimitCap(rpmRaw).kind === 'empty' && parseTpmM(tpmRaw).kind === 'empty';
}

/** The #2738 uncached-TPM draft: millions of tokens, like TPM. */
export interface UncachedTpmDraft {
  readonly raw: string;
  readonly original?: number | null;
}

/**
 * Build the PUT body (or delete) for one rate-limit layer. The body always
 * carries `tpm_uncached` explicitly (null when blank), because the write face
 * treats an absent key as "keep the stored value"; the row is only deleted
 * when all three dimensions are blank. `originalTpm` / `uncached.original`
 * are the stored token integers, so an untouched M-field round-trips exactly.
 */
export function buildRateLimitWrite(
  rpmRaw: string,
  tpmRaw: string,
  originalTpm: number | null | undefined,
  uncached: UncachedTpmDraft,
): RateLimitDraftResult {
  const rpm = parseRateLimitCap(rpmRaw);
  const tpm = parseTpmMPreserving(tpmRaw, originalTpm);
  const tpmUncached = parseTpmMPreserving(uncached.raw, uncached.original);
  if (rpm.kind === 'invalid' || tpm.kind === 'invalid' || tpmUncached.kind === 'invalid') {
    return {
      kind: 'invalid',
      rpm: rpm.kind === 'invalid',
      tpm: tpm.kind === 'invalid',
      tpmUncached: tpmUncached.kind === 'invalid',
    };
  }
  if (rpm.kind === 'empty' && tpm.kind === 'empty' && tpmUncached.kind === 'empty') {
    return { kind: 'delete' };
  }
  const body: PutRateLimitRequest = {
    rpm: rpm.kind === 'value' ? rpm.value : null,
    tpm: tpm.kind === 'value' ? tpm.value : null,
    tpm_uncached: tpmUncached.kind === 'value' ? tpmUncached.value : null,
  };
  return { kind: 'put', body };
}

